import {
  string as yupString,
  boolean as yupBoolean,
  mixed as yupMixed,
  addMethod,
} from 'yup';

import InputFormik from '../../shared/forms/input/inputFormik.component';
import CheckboxFormik from '../../shared/forms/checkbox/checkboxFormik.component';
import SelectFormik from '../../shared/forms/select/sdkSelectFormik.component';
import AttachmentsFormik from '../../shared/forms/attachments/attachmentsFormik.component';
import { required, maxLength, yesNoFlag, underscoredAlphaNumeric } from '../../shared/forms/validators';
import { YES_FLAG, NO_FLAG } from '../../shared/constants';

import {
  CONVERSION_RATES_SOURCES,
  LEGAL_ENTITIES_SOURCES,
  COMPANY_CODES_SOURCES,
  COUNTRY_CODES_SOURCES,
  enabledFlagOptions,
  monthAbbr,
} from './constants';

addMethod(yupString, 'noDuplicates', function(message) {
  return this.test('noDuplicates', message, function(value) {
    const { path, createError } = this;

    if (value) {
      const domains = value.split(',').map(domain => domain.trim());
      const uniqueDomains = new Set(domains);

      if (domains.length !== uniqueDomains.size) {
        return createError({ path, message });
      }
    }

    return true;
  });
});

const schema = ({ featureFlags }) => [
  {
    name: 'name',
    component: InputFormik,
    meta: { label: 'Name' },
    options: { yupType: yupString(), validationRules: [required, maxLength(50)] },
    additionalProps: { required: true },
  },
  {
    name: 'shortName',
    component: InputFormik,
    meta: { label: 'Short Name (Characters, no spaces, max 10)' },
    options: { yupType: yupString(), validationRules: [required, underscoredAlphaNumeric, maxLength(10)] },
    additionalProps: { required: true },
  },
  {
    name: 'enabledFlag',
    component: SelectFormik,
    meta: { label: 'Enabled', options: enabledFlagOptions, initialValue: NO_FLAG },
    options: { yupType: yupString(), validationRules: [required, yesNoFlag] },
    additionalProps: { required: true },
  },
  {
    name: 'conversionRateSource',
    component: SelectFormik,
    meta: {
      label: 'Conversion Rates Source',
      options: Object.values(CONVERSION_RATES_SOURCES),
      initialValue: CONVERSION_RATES_SOURCES.TLS.value,
    },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'legalEntitiesSource',
    component: SelectFormik,
    meta: {
      label: 'Legal Entities Source',
      options: Object.values(LEGAL_ENTITIES_SOURCES),
      initialValue: LEGAL_ENTITIES_SOURCES.TLS.value,
    },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'companyCodesSource',
    component: SelectFormik,
    meta: {
      label: 'Company Codes Source',
      options: Object.values(COMPANY_CODES_SOURCES),
      initialValue: COMPANY_CODES_SOURCES.TLS.value,
    },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'countryCodesSource',
    component: SelectFormik,
    meta: {
      label: 'Country Codes Source',
      options: Object.values(COUNTRY_CODES_SOURCES),
      initialValue: COUNTRY_CODES_SOURCES.INC.value,
    },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true, virtualized: true },
  },
  {
    name: 'emailDomain',
    component: InputFormik,
    meta: { label: 'Email Domains (separate multiple domains with a comma)' },
    options: { yupType: yupString().noDuplicates('There are repeated domains in the input.').required('Email domains are required.') },
    additionalProps: { required: true },
  },
  {
    name: 'supportEmail',
    component: InputFormik,
    meta: { label: 'Support Email' },
    options: { yupType: yupString() },
  },
  {
    name: 'notificationEmail',
    component: InputFormik,
    meta: { label: 'Notification Email' },
    options: { yupType: yupString() },
  },
  {
    name: 'reportFolder',
    component: InputFormik,
    meta: { label: 'Report Folder' },
    options: { yupType: yupString(), validationRules: [maxLength(200)] },
  },
  {
    name: 'DBLogDetail',
    component: SelectFormik,
    meta: { label: 'DB Log Detail', options: enabledFlagOptions, initialValue: YES_FLAG },
    options: { yupType: yupString(), validationRules: [required, yesNoFlag] },
    additionalProps: { required: true },
  },
  {
    name: 'fiscalStartMonth',
    component: SelectFormik,
    meta: { label: 'Fiscal Start Month', options: Object.values(monthAbbr), initialValue: 'JAN' },
    options: { yupType: yupString() },
  },
  {
    name: 'pwcClientId',
    component: InputFormik,
    meta: { label: 'PWC Client ID' },
    options: { yupType: yupString() },
  },
  {
    name: 'pwcEngagementId',
    component: InputFormik,
    meta: { label: 'PWC Engagement ID' },
    options: { yupType: yupString() },
  },
  {
    name: 'istClientId',
    component: InputFormik,
    meta: { label: 'IST Client ID' },
    options: { yupType: yupString() },
  },
  {
    name: 'cesClientPrid',
    component: InputFormik,
    meta: { label: 'CES Client PRID' },
    options: { yupType: yupString() },
  },
  {
    name: 'startPeriod',
    component: InputFormik,
    meta: { label: 'Starting Period (MON-YY)' },
    options: { yupType: yupString(), validationRules: [required, maxLength(6)] },
    additionalProps: { required: true },
  },
  ...featureFlags.map(({ field, label }) =>
    ({
      name: field,
      component: CheckboxFormik,
      meta: { label, initialValue: false },
      options: { yupType: yupBoolean() },
      additionalProps: { valueFormatter: Boolean },
    })),
  {
    name: 'files',
    component: AttachmentsFormik,
    meta: { label: 'Logo', initialValue: null },
    options: { yupType: yupMixed() },
    additionalProps: {
      acceptTypes: ['.jpg', '.jpeg', '.png'],
      withThumbnails: true,
      valueFormatter: file => file,
    },
  },
];

export default schema;
